import { useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Card, Field, Grid, ReadOnlyNote, inputCls, btnPrimary } from "./shared";

const DAYS = [["lun", "Lunedì"], ["mar", "Martedì"], ["mer", "Mercoledì"], ["gio", "Giovedì"],
  ["ven", "Venerdì"], ["sab", "Sabato"], ["dom", "Domenica"]];

function DayEditor({ day, onChange }) {
  const set = (k, v) => onChange({ ...day, [k]: v });
  const setTurno = (i, k, v) => {
    const turni = day.turni.map((t, j) => (j === i ? { ...t, [k]: v } : t));
    onChange({ ...day, turni });
  };
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm text-zinc-700">
          <input type="checkbox" checked={day.aperto} onChange={(e) => set("aperto", e.target.checked)}
            className="w-4 h-4 accent-zinc-900" data-testid="orari-aperto" /> Giorno aperto
        </label>
        <label className="flex items-center gap-2 text-sm text-zinc-700">
          <input type="checkbox" checked={day.prenotazioni_consentite}
            onChange={(e) => set("prenotazioni_consentite", e.target.checked)}
            className="w-4 h-4 accent-zinc-900" data-testid="orari-prenotazioni" /> Consenti prenotazioni
        </label>
      </div>
      {day.aperto && (
        <div className="space-y-2">
          {day.turni.map((t, i) => (
            <div key={i} className="flex items-end gap-2 flex-wrap" data-testid={`turno-${i}`}>
              <Field label="Turno">
                <input value={t.nome} onChange={(e) => setTurno(i, "nome", e.target.value)}
                  className={`${inputCls} w-28`} data-testid={`turno-${i}-nome`} />
              </Field>
              <Field label="Da">
                <input type="time" value={t.da} onChange={(e) => setTurno(i, "da", e.target.value)}
                  className={`${inputCls} w-28`} data-testid={`turno-${i}-da`} />
              </Field>
              <Field label="A">
                <input type="time" value={t.a} onChange={(e) => setTurno(i, "a", e.target.value)}
                  className={`${inputCls} w-28`} data-testid={`turno-${i}-a`} />
              </Field>
              <Field label="Slot (min)">
                <input type="number" min="5" step="5" value={t.slot_minuti}
                  onChange={(e) => setTurno(i, "slot_minuti", parseInt(e.target.value) || 30)}
                  className={`${inputCls} w-24`} data-testid={`turno-${i}-slot`} />
              </Field>
              <button type="button" onClick={() => onChange({ ...day, turni: day.turni.filter((_, j) => j !== i) })}
                className="h-11 w-11 rounded-md border border-rose-200 text-rose-600 flex items-center justify-center"
                data-testid={`turno-${i}-delete`}><Trash size={16} /></button>
            </div>
          ))}
          <button type="button"
            onClick={() => onChange({ ...day, turni: [...day.turni, { nome: "Turno", da: "12:00", a: "15:00", slot_minuti: 30 }] })}
            className="h-9 px-3 rounded-md border border-zinc-300 text-xs font-semibold text-zinc-700 flex items-center gap-1.5"
            data-testid="orari-add-turno"><Plus size={14} weight="bold" /> Aggiungi turno</button>
        </div>
      )}
    </div>
  );
}

export default function OrariSection({ settings, onSave, saving, readOnly }) {
  const [o, setO] = useState(settings.orari || {});
  const [day, setDay] = useState("mar");
  const [ecc, setEcc] = useState({ da: "", a: "", motivo: "" });
  const giorni = o.giorni || {};
  const cg = o.carta_garanzia || {};
  const eccezioni = o.eccezioni || [];
  const setDayData = (d, val) => setO({ ...o, giorni: { ...giorni, [d]: val } });
  const setCg = (k, v) => setO({ ...o, carta_garanzia: { ...cg, [k]: v } });
  const addEccezione = () => {
    if (!ecc.da) { toast.error("Seleziona almeno la data di inizio"); return; }
    const item = { da: ecc.da, a: ecc.a || ecc.da, motivo: ecc.motivo.trim() };
    if (item.a < item.da) { toast.error("La data di fine non può precedere l'inizio"); return; }
    setO({ ...o, eccezioni: [...eccezioni, item].sort((x, y) => x.da.localeCompare(y.da)) });
    setEcc({ da: "", a: "", motivo: "" });
  };

  return (
    <div className="space-y-5" data-testid="setsection-orari">
      <Card title="Orari settimanali e turni"
        desc="Giorni di apertura, fasce orarie e slot di prenotazione. Collegati al sistema di prenotazione."
        testid="orari-card">
        <div className="flex gap-1.5 flex-wrap" data-testid="orari-days">
          {DAYS.map(([k, l]) => (
            <button key={k} onClick={() => setDay(k)} data-testid={`orari-day-${k}`}
              className={`h-9 px-3 rounded-md text-xs font-bold border transition-colors ${
                day === k ? "bg-zinc-900 text-white border-zinc-900"
                  : giorni[k]?.aperto ? "bg-white text-zinc-700 border-zinc-300" : "bg-zinc-100 text-zinc-400 border-zinc-200"}`}>
              {l.slice(0, 3)}{!giorni[k]?.aperto && " · chiuso"}
            </button>
          ))}
        </div>
        {giorni[day] && <DayEditor day={giorni[day]} onChange={(v) => setDayData(day, v)} />}
      </Card>

      <Card title="Chiusure ed eccezioni"
        desc="Periodi di chiusura (ferie, festività) o singoli giorni chiusi. In queste date le prenotazioni online sono bloccate, anche se il giorno sarebbe aperto."
        testid="eccezioni-card">
        {eccezioni.length > 0 && (
          <div className="space-y-2 mb-4" data-testid="eccezioni-list">
            {eccezioni.map((e, i) => (
              <div key={i} className="flex items-center gap-3 border border-rose-200 bg-rose-50 rounded-lg px-3 py-2"
                data-testid={`eccezione-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-zinc-900">
                    {e.da === e.a ? e.da : `dal ${e.da} al ${e.a}`}
                  </div>
                  {e.motivo && <div className="text-xs text-zinc-500 truncate">{e.motivo}</div>}
                </div>
                {!readOnly && (
                  <button type="button" onClick={() => setO({ ...o, eccezioni: eccezioni.filter((_, j) => j !== i) })}
                    className="p-1.5 rounded-md text-rose-500 hover:bg-rose-100" data-testid={`eccezione-${i}-delete`}>
                    <Trash size={15} weight="bold" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {!readOnly && (
          <div className="flex items-end gap-2 flex-wrap" data-testid="eccezioni-form">
            <Field label="Dal giorno">
              <input type="date" value={ecc.da} onChange={(e) => setEcc({ ...ecc, da: e.target.value })}
                className={`${inputCls} w-40`} data-testid="eccezione-da" />
            </Field>
            <Field label="Al giorno (vuoto = solo quel giorno)">
              <input type="date" value={ecc.a} onChange={(e) => setEcc({ ...ecc, a: e.target.value })}
                className={`${inputCls} w-40`} data-testid="eccezione-a" />
            </Field>
            <Field label="Motivo (opzionale)">
              <input value={ecc.motivo} onChange={(e) => setEcc({ ...ecc, motivo: e.target.value })}
                className={`${inputCls} w-52`} placeholder="Es. Ferie estive" data-testid="eccezione-motivo" />
            </Field>
            <button type="button" onClick={addEccezione}
              className="h-11 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5"
              data-testid="eccezione-add"><Plus size={14} weight="bold" /> Aggiungi chiusura</button>
          </div>
        )}
        <p className="text-[11px] text-zinc-400 mt-3">Ricorda di salvare con il pulsante in basso per applicare le chiusure.</p>
      </Card>

      <Card title="Carta di credito a garanzia e caparra"
        desc="Definisci quando richiedere una garanzia o un prepagamento sulle prenotazioni."
        testid="carta-garanzia-card">
        <label className="flex items-center gap-2 text-sm text-zinc-700">
          <input type="checkbox" checked={!!cg.attiva} onChange={(e) => setCg("attiva", e.target.checked)}
            className="w-4 h-4 accent-zinc-900" data-testid="cg-attiva" /> Richiedi carta di credito a garanzia
        </label>
        {cg.attiva && (
          <>
            <Grid>
              <Field label="Quando è richiesta">
                <select value={cg.quando || "soglia"} onChange={(e) => setCg("quando", e.target.value)}
                  className={inputCls} data-testid="cg-quando">
                  <option value="sempre">Sempre</option>
                  <option value="soglia">Sopra una soglia di coperti</option>
                  <option value="weekend">Nel weekend</option>
                  <option value="eventi">Solo per eventi</option>
                </select>
              </Field>
              {cg.quando === "soglia" && (
                <Field label="Soglia coperti">
                  <input type="number" min="2" value={cg.soglia_coperti ?? 8}
                    onChange={(e) => setCg("soglia_coperti", parseInt(e.target.value) || 8)}
                    className={inputCls} data-testid="cg-soglia" />
                </Field>
              )}
              <Field label="Tipo importo">
                <select value={cg.tipo_importo || "fisso"} onChange={(e) => setCg("tipo_importo", e.target.value)}
                  className={inputCls} data-testid="cg-tipo">
                  <option value="fisso">Importo fisso</option>
                  <option value="percentuale">Percentuale</option>
                </select>
              </Field>
              <Field label="Importo">
                <input type="number" min="0" value={cg.importo ?? 20}
                  onChange={(e) => setCg("importo", parseFloat(e.target.value) || 0)}
                  className={inputCls} data-testid="cg-importo" />
              </Field>
            </Grid>
            <Field label="Condizioni carta a garanzia">
              <textarea value={cg.condizioni || ""} onChange={(e) => setCg("condizioni", e.target.value)}
                rows={3} className={`${inputCls} h-auto py-2`} data-testid="cg-condizioni"
                placeholder="Es. addebito solo in caso di no-show senza disdetta entro 24h…" />
            </Field>
          </>
        )}
      </Card>

      {readOnly ? <ReadOnlyNote /> : (
        <button disabled={saving} onClick={() => onSave({ orari: o })} className={btnPrimary} data-testid="orari-save">
          Salva Orari e turni
        </button>
      )}
    </div>
  );
}
